<!--
        selection-type : 单选/复选/关闭 模式，具体：
                          - radio：单选模式（无复选框，通过行高亮标识选中行，点击行触发选中状态切换，同步触发current-row-change和selection-change事件）；
                          - checkbox：多选模式（显示复选框列，可勾选多行，点击行也可切换复选框状态，selection-change事件返回选中行数组）；
                          - none：无选择交互（隐藏选择相关逻辑，不触发任何选择事件）；
                          本场景选radio，适配角色列表「单条编辑/删除/配置菜单」的业务诉求。
        row-key ：表格行的唯一标识字段，底层绑定el-table的row-key属性，用于：
                  1. 行选择/高亮的唯一性判断（如radio模式匹配当前行、checkbox模式判断选中状态）；
                  2. Vue列表渲染的key优化，避免行渲染异常；
                  本场景指定为roleId，因角色数据的唯一标识为roleId（非默认id）。
        has-more-search : 是否显示搜索扩展区的展开/收起按钮，true显示、false隐藏；
                          本场景为true，因角色列表包含「固定搜索项（角色ID/名称/是否生效）+ 扩展搜索项（创建时间/更新时间）」，需支持扩展搜索。
        auto-load : 组件挂载后是否自动触发数据加载，true则onMounted时触发load-start事件，false需手动触发；
                    本场景为true，实现角色列表页面加载后默认展示数据。
        ref : 父组件获取CommonList组件实例的标识，用于调用组件暴露的方法（如updateListData更新表格数据、setLoading设置加载状态、updatePagination更新分页）；
              本场景为listRef，RoleList中通过该ref操作通用列表的核心状态/数据。
        cellOverflow : 表格单元格内容溢出处理方式，ellipsis为单行省略（默认）、wrap为换行展示；
                       本场景为ellipsis，保证角色列表单元格（如角色描述）单行展示，避免行高不一致。
        @selection-change : 选中项变更时触发的事件，参数为选中行数组（radio模式为[当前行]/[]，checkbox模式为选中行集合）；
                            本场景绑定handleSelectionChange，同步更新selectedRow变量，为编辑/删除按钮禁用状态提供依据。
        @current-row-change : 仅radio模式下触发，表格原生current-change事件（切换高亮行时）；
                              本场景绑定handleCurrentRowChange，作为selection-change的补充，强化单选行状态的准确性。
        @load-start : 组件初始化自动加载时触发的事件（auto-load为true时）；
                      本场景绑定handleLoadList，与查询/分页复用同一加载方法，实现初始化加载角色数据。
        @search-click : 点击「查询」按钮时触发的事件，触发前会重置页码为1；
                        本场景绑定handleLoadList，实现搜索条件变更后重新加载角色列表。
        @pagination-change : 分页参数变更时触发（切换页码/调整页大小），参数为最新分页信息；
                             本场景绑定handleLoadList，实现分页切换时加载对应页的角色数据。
        @reset-click : 点击「重置」按钮时触发的事件，触发前会重置页码为1；
                       本场景绑定handleReset，实现清空搜索表单并重新加载无筛选的角色列表。
-->

<!-- 通用列表公共组件：封装搜索区、表格、分页逻辑，支持单选/多选/无选择框，分页嵌入表格底部 -->
<template>
    <div class="common-list-container" ref="containerRef">
        <!-- 搜索区域：分为固定展示区+可展开/收起的扩展区 -->
        <div class="common-list-search-box">
            <div class="common-search-header">
                <!-- 搜索固定区：默认展示的搜索项 -->
                <div class="common-search-fixed">
                    <slot name="search-item-fixed"></slot>
                </div>
                <!-- 搜索操作按钮组：查询、重置、展开/收起 -->
                <div class="common-search-actions">
                    <el-button type="primary" @click="handleSearchClick">查询</el-button>
                    <el-button @click="handleResetClick">重置</el-button>
                    <el-button
                            v-if="hasMoreSearch"
                            type="text"
                            class="expand-btn"
                            @click="isSearchExpand = !isSearchExpand"
                            :class="{ 'rotate-icon': isSearchExpand }"
                    >
                        <i class="el-icon el-icon-arrow-down">
                            <el-icon size="20" color="#409eff">
                                <CaretTop/>
                            </el-icon>
                        </i>
                    </el-button>
                </div>
            </div>
            <!-- 搜索扩展区：需手动展开显示更多搜索项 -->
            <div
                    class="common-search-expand"
                    v-show="hasMoreSearch && isSearchExpand"
            >
                <slot name="search-item-expand"></slot>
            </div>
        </div>

        <!-- 操作按钮组插槽：自定义表格上方的功能按钮 -->
        <div class="common-list-btn-group">
            <slot name="btn-group"></slot>
        </div>

        <!-- 表格容器：移除高度计算，分页嵌入表格底部 -->
        <div class="common-list-table-wrapper">
            <el-table
                    class="common-list-table"
                    :data="innerTableData"
                    border
                    stripe
                    v-loading="innerLoading"
                    @selection-change="handleSelectionChange"
                    @current-change="handleCurrentRowChange"
                    @row-click="handleRowClick"
                    :row-key="rowKey"
                    ref="tableRef"
                    :show-overflow-tooltip="true"
                    :class="`cell-overflow-${cellOverflow}`"
                    :row-class-name="rowClassName"
                    :highlight-current-row="selectionType === 'radio'"
                    :select-on-indeterminate="false"
            >
                <!-- 表格空状态自定义：加载中/暂无数据 -->
                <template #empty>
                    <div class="table-empty-wrap">
                        <el-icon size="40" color="#ccc">
                            <Empty/>
                        </el-icon>
                        <p style="margin-top: 12px; font-size: 14px; color: #999;">{{ innerLoading ? '加载中...' : '暂无数据' }}</p>
                    </div>
                </template>

                <!-- 选择列控制：仅checkbox类型显示复选框列；radio靠原生高亮，none不显示选择列 -->
                <el-table-column
                        v-if="selectionType === 'checkbox'"
                        type="selection"
                        width="55"
                        align="center"
                ></el-table-column>

                <!-- 表格列插槽：自定义表格列配置 -->
                <slot name="table-column"></slot>

                <!-- 分页嵌入表格底部：仅表格有数据且非加载中时显示 -->
                <template #append>
                    <div
                            class="common-list-pagination"
                            v-if="innerTableData.length > 0 && !innerLoading"
                    >
                        <el-pagination
                                v-model:current-page="pagination.pageNum"
                                v-model:page-size="pagination.pageSize"
                                :page-sizes="[10, 20, 50, 100]"
                                :total="pagination.total"
                                layout="total, sizes, prev, pager, next, jumper"
                                @size-change="handleSizeChange"
                                @current-change="handleCurrentChange"
                        ></el-pagination>
                    </div>
                </template>
            </el-table>
        </div>

        <!-- 额外内容插槽：自定义组件底部内容 -->
        <slot name="extra"></slot>
    </div>
</template>

<script setup>
    import {
        defineEmits,
        defineExpose,
        defineProps,
        nextTick,
        onMounted,
        onUnmounted,
        reactive,
        ref,
        toRefs,
        watch
    } from 'vue'

    // Props定义：核心配置项
    const props = defineProps({
        // 选择类型：none(无选择交互)、radio(单选/高亮)、checkbox(多选/复选框)
        selectionType: {
            type: String,
            default: 'radio',
            validator: (val) => ['none', 'radio', 'checkbox'].includes(val)
        },
        // 表格行唯一标识字段，用于行选择/高亮判断
        rowKey: {
            type: String,
            default: 'id'
        },
        // 是否存在更多搜索项（控制展开/收起按钮显示）
        hasMoreSearch: {
            type: Boolean,
            default: false
        },
        // 初始分页配置：pageNum(当前页)、pageSize(页大小)、total(总条数)
        initialPagination: {
            type: Object,
            default: () => ({pageNum: 1, pageSize: 10, total: 0})
        },
        // 单元格内容溢出处理：ellipsis(单行省略)、wrap(换行展示)
        cellOverflow: {
            type: String,
            default: 'ellipsis',
            validator: (val) => ['ellipsis', 'wrap'].includes(val)
        },
        // 是否在组件挂载后自动触发数据加载
        autoLoad: {
            type: Boolean,
            default: true
        }
    })

    // 事件定义：向外暴露的事件
    const emit = defineEmits([
        'selection-change', // 选择项变更（单选/多选均触发）
        'current-row-change', // 单选当前行变更
        'search-click', // 点击查询按钮
        'reset-click', // 点击重置按钮
        'pagination-change', // 分页参数变更（页码/页大小）
        'load-start' // 组件初始化自动加载触发
    ])

    // 响应式数据解构：简化props访问
    const {
        selectionType, rowKey, hasMoreSearch, initialPagination: initialPaginationProp,
        autoLoad
    } = toRefs(props)

    // 分页参数：响应式存储，基于初始配置初始化
    const pagination = reactive({...initialPaginationProp.value})
    // 搜索扩展区展开状态
    const isSearchExpand = ref(false)
    // 表格实例引用：用于调用表格原生方法（如setCurrentRow、doLayout）
    const tableRef = ref(null)
    // 组件容器引用
    const containerRef = ref(null)
    // 内部加载状态：控制表格loading显示
    const innerLoading = ref(false)
    // 表格内部数据：父组件通过updateListData更新
    const innerTableData = ref([])
    // 初始化完成标识：标记组件挂载完成
    const isInited = ref(false)
    // 多选选中行集合：存储checkbox类型选中的行
    const selection = ref([])
    // 单选当前行：存储radio类型选中的行
    const currentRow = ref(null)

    // 对外暴露方法：父组件可调用更新数据/状态
    // 更新表格展示数据
    const updateListData = (data) => {
        innerTableData.value = data || []
    }

    // 更新分页参数（页码/页大小/总条数）
    const updatePagination = (pageInfo) => {
        pagination.pageNum = pageInfo.pageNum || 1
        pagination.pageSize = pageInfo.pageSize || 10
        pagination.total = pageInfo.total || 0
    }

    // 设置表格加载状态
    const setLoading = (status) => {
        innerLoading.value = status
    }

    // 事件处理方法
    // 点击查询按钮：重置页码为1，触发search-click事件
    const handleSearchClick = () => {
        pagination.pageNum = 1
        emit('search-click', {...pagination})
    }

    // 点击重置按钮：重置页码为1，触发reset-click事件
    const handleResetClick = () => {
        pagination.pageNum = 1
        emit('reset-click', {...pagination})
    }

    // 多选选中变更：更新选中集合，触发selection-change事件
    const handleSelectionChange = (val) => {
        selection.value = val
        emit('selection-change', val)
    }

    // 单选当前行变更：更新当前行，同步触发selection-change（保持交互统一）
    const handleCurrentRowChange = (val) => {
        currentRow.value = val
        emit('selection-change', val ? [val] : [])
        emit('current-row-change', val)
    }

    // 表格行点击处理：不同选择类型触发不同交互
    const handleRowClick = (row) => {
        if (selectionType.value === 'radio') {
            // 单选：设置表格原生高亮行
            tableRef.value.setCurrentRow(row)
        } else if (selectionType.value === 'checkbox') {
            // 多选：点击行切换选中状态（无需点击复选框）
            const table = tableRef.value
            const isSelected = selection.value.some(item => item[rowKey.value] === row[rowKey.value])
            table.toggleRowSelection(row, !isSelected)
        }
        // none类型：无交互处理
    }

    // 分页页大小变更：更新pageSize，触发pagination-change事件
    const handleSizeChange = (val) => {
        pagination.pageSize = val
        emit('pagination-change', {...pagination})
    }

    // 分页页码变更：更新pageNum，触发pagination-change事件
    const handleCurrentChange = (val) => {
        pagination.pageNum = val
        emit('pagination-change', {...pagination})
    }

    // 表格行类名自定义：控制选中行高亮样式
    const rowClassName = ({row}) => {
        const rowId = row[rowKey.value]
        // none类型：无样式
        if (selectionType.value === 'none') return ''

        // radio类型：当前行添加高亮类
        if (selectionType.value === 'radio') {
            return currentRow?.value?.[rowKey.value] === rowId ? 'common-row-selected' : ''
        }

        // checkbox类型：选中行添加高亮类
        if (selectionType.value === 'checkbox') {
            return selection.value.some(item => item[rowKey.value] === rowId) ? 'common-row-selected' : ''
        }

        return ''
    }

    // 生命周期：挂载完成
    onMounted(() => {
        nextTick(() => {
            isInited.value = true // 标记初始化完成
            if (autoLoad.value) {
                emit('load-start', {...pagination}) // 自动触发加载
            }
        })
    })

    onUnmounted(() => {
        // 无清理逻辑，保留空方法
    })

    // 监听搜索展开状态：展开/收起后刷新表格布局，避免表格渲染异常
    watch(isSearchExpand, () => {
        nextTick(() => {
            if (tableRef.value) tableRef.value.doLayout()
        })
    })

    const restoreSelection = (rows) => {
        if (!tableRef.value || !rows) return
        nextTick(() => {
            rows.forEach(row => {
                tableRef.value.toggleRowSelection(row, true)
            })
        })
    }

    // 暴露给父组件的属性/方法
    defineExpose({
        isInited, // 初始化状态标识
        updateListData, // 更新表格数据
        updatePagination, // 更新分页信息
        setLoading, // 设置加载状态
        getPagination: () => ({...pagination}), // 获取当前分页参数（返回新对象避免响应式污染）
        getTableData: () => innerTableData.value, // 获取当前表格数据
        restoreSelection // 恢复选中行
    })
</script>

<style scoped>
    /* 组件容器样式：基础布局、内边距、阴影 */
    .common-list-container {
        padding: 24px;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.04);
        min-height: calc(100vh - 48px);
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    /* 搜索区样式：背景、边框、布局 */
    .common-list-search-box {
        padding: 20px;
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        gap: 16px;
        background: #fafafa;
    }

    .common-search-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;
        min-height: 48px;
    }

    /* 搜索固定区：flex布局，支持换行 */
    .common-search-fixed {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 16px 24px;
        flex: 1;
        min-height: 48px;
    }

    /* 搜索扩展区：与固定区分割，顶部虚线边框 */
    .common-search-expand {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 16px 24px;
        padding-top: 12px;
        border-top: 1px dashed #e8e8e8;
        width: 100%;
    }

    /* 搜索操作按钮组：靠右，固定宽度不收缩 */
    .common-search-actions {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        flex-shrink: 0;
        /*padding-top: 6px;*/
    }

    /* 展开/收起按钮样式：圆形、hover交互 */
    .expand-btn {
        width: 38px;
        height: 34px;
        padding: 0;
        border-radius: 6px;
        color: #409eff;
        background: transparent !important;
        border: 1px solid transparent !important;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }

    .expand-btn:hover {
        background: rgba(64, 158, 255, 0.1) !important;
        color: #1989fa;
        border-color: #e8f4ff !important;
    }

    /* 展开/收起按钮图标：旋转动画 */
    :deep(.expand-btn .el-icon-arrow-down) {
        font-size: 18px;
        transition: transform 0.2s ease-in-out;
        transform-origin: center;
    }

    :deep(.rotate-icon .el-icon-arrow-down) {
        transform: rotate(180deg);
    }

    /* 表格容器：占满剩余空间，圆角溢出隐藏 */
    .common-list-table-wrapper {
        border-radius: 6px;
        flex: 1;
        overflow: hidden;
    }

    /* 表格基础样式：宽度、最小宽度、表头样式 */
    .common-list-table {
        width: 100%;
        min-width: 800px;
        --el-table-header-text-color: #333;
        --el-table-row-hover-bg-color: #f5f8ff; /* 行hover底色，与选中色区分 */
        border-radius: 6px;
        overflow: hidden;
    }

    /* 表格空状态样式：居中、图标、文字 */
    .table-empty-wrap {
        padding: 60px 20px;
        text-align: center;
        color: #999;
    }

    /* 单元格溢出样式：ellipsis(单行省略)、wrap(换行) */
    :deep(.cell-overflow-ellipsis .el-table-cell) {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        padding: 12px 0;
    }

    :deep(.cell-overflow-wrap .el-table-cell) {
        white-space: normal;
        line-height: 1.6;
        padding: 12px 0;
    }

    /* 表头样式：背景色、加粗、底部边框 */
    :deep(.common-list-table thead th) {
        background: #f8f9fa;
        font-weight: 600;
        border-bottom: 2px solid #e8e8e8;
    }

    /* 操作按钮组：靠右排列，间距 */
    .common-list-btn-group {
        flex-shrink: 0;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
    }

    /* 分页样式：嵌入表格底部，背景色，顶部边框 */
    :deep(.common-list-pagination) {
        padding: 16px 20px;
        text-align: right;
        background: #fafafa;
        border-top: 1px solid #e8e8e8;
        display: flex;
        justify-content: flex-end;
    }

    :deep(.common-list-pagination .el-pagination) {
        margin: 0;
    }

    :deep(.el-pagination__total) {
        color: #666;
        font-size: 13px;
    }

    :deep(.el-pagination button:disabled) {
        color: #ccc !important;
    }

    :deep(.el-pagination .el-pager li) {
        margin: 0 4px;
        border-radius: 4px;
    }

    :deep(.el-pagination .el-pager li.active) {
        background: #409eff;
        color: #fff;
    }

    /* 按钮通用样式：圆角、字号 */
    :deep(.common-search-actions .el-button) {
        padding: 8px 16px;
        border-radius: 6px;
        font-size: 13px;
    }

    :deep(.common-list-btn-group .el-button) {
        padding: 9px 20px;
        border-radius: 6px;
        font-size: 13px;
    }

    /* 选中行高亮样式：优先级最高，覆盖原生样式 */
    :deep(.common-row-selected) {
        position: relative;
        z-index: 1;
    }
    :deep(.common-row-selected > td) {
        background-color: var(--el-color-primary-light-8) !important;
    }
    :deep(.common-row-selected:hover > td) {
        background-color: var(--el-color-primary-light-8) !important;
    }

    /* 单选原生高亮行样式：复用选中行颜色，左侧加边框标识 */
    :deep(.el-table--highlight-current-row .el-table__row.current-row > td) {
        background-color: var(--el-color-primary-light-8) !important;
        box-shadow: inset 2px 0 0 0 var(--el-color-primary) !important;
    }
    :deep(.el-table--highlight-current-row .el-table__row.current-row:hover > td) {
        background-color: var(--el-color-primary-light-8) !important;
    }

    /* 非选中行hover样式：保留基础交互 */
    :deep(.el-table__row:hover:not(.common-row-selected):not(.current-row) > td) {
        background-color: var(--el-table-row-hover-bg-color) !important;
    }


/* ==================== 手机端搜索区响应式样式 ==================== */
@media (max-width: 768px) {
    /* 手机端容器调整 */
    .common-list-container {
        padding: 12px;
        gap: 12px;
        min-height: auto;
    }

    /* 手机端搜索区调整 */
    .common-list-search-box {
        padding: 12px;
        gap: 12px;
    }

    /* 手机端搜索头部改为垂直排列 */
    .common-search-header {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    /* 手机端搜索固定区：搜索条件自动换行，名称和框在同一行 */
    .common-search-fixed {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 12px 20px;
        width: 100%;
        justify-content: flex-start;
    }

    /* 搜索固定区只有一个条件时居中 */
    .common-search-fixed:has(.common-search-item:only-child) {
        justify-content: center;
    }

    /* 手机端搜索项：名称和框在同一行 */
    .common-search-fixed .common-search-item {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 0 1 auto;
        min-width: 200px;
        max-width: 100%;
    }

    /* 搜索项内部布局：标签和输入框在同一行 */
    .common-search-fixed .common-search-item .common-search-label80 {
        flex-shrink: 0;
        white-space: nowrap;
    }

    .common-search-fixed .common-search-item .el-input,
    .common-search-fixed .common-search-item .el-select {
        flex: 1;
        min-width: 120px;
    }

    /* 手机端搜索按钮组：在搜索固定区下方，横向居中 */
    .common-search-actions {
        width: 100%;
        justify-content: center;
        padding-top: 8px;
    }

    /* 手机端搜索扩展区样式与固定区保持一致 */
    .common-search-expand {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 12px 20px;
        padding-top: 12px;
        justify-content: flex-start;
    }

    /* 搜索扩展区只有一个条件时居中 */
    .common-search-expand:has(.common-search-item:only-child) {
        justify-content: center;
    }

    .common-search-expand .common-search-item {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 0 1 auto;
        min-width: 200px;
        max-width: 100%;
    }

    /* 搜索扩展项内部布局：标签和输入框在同一行 */
    .common-search-expand .common-search-item .common-search-label80 {
        flex-shrink: 0;
        white-space: nowrap;
    }

    .common-search-expand .common-search-item .el-input,
    .common-search-expand .common-search-item .el-select {
        flex: 1;
        min-width: 120px;
    }

    /* 手机端按钮组调整 */
    .common-list-btn-group {
        justify-content: center;
        flex-wrap: wrap;
    }

    /* 手机端表格容器调整 */
    .common-list-table-wrapper {
        overflow-x: auto;
    }

    /* 手机端表格最小宽度调整 */
    .common-list-table {
        min-width: 600px;
    }

    /* 手机端分页调整 */
    :deep(.common-list-pagination) {
        padding: 12px 8px;
        overflow-x: auto;
        justify-content: center;
    }

    :deep(.common-list-pagination .el-pagination) {
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
    }
}

@media (max-width: 480px) {
    /* 小屏手机进一步优化 */
    .common-list-container {
        padding: 8px;
    }

    .common-list-search-box {
        padding: 10px;
    }

    :deep(.common-search-actions .el-button) {
        padding: 8px 14px;
        font-size: 13px;
    }

    :deep(.common-list-btn-group .el-button) {
        padding: 8px 14px;
        font-size: 13px;
    }
}
</style>